import React from 'react';
import { useLoaderData } from 'react-router-dom';
import { VaBreadcrumbs } from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import api from '../utilities/api';
import ClaimantDetailsWrapper from '../components/ClaimantDetailsWrapper';
import {
  claimantOverviewBC,
  requestsContainStatus,
} from '../utilities/poaRequests';
import KeyValueTable from '../components/KeyValueTable';
import ClaimantNotFound from '../components/ClaimantNotFound';

const toTitleCase = value => {
  if (!value) return '';

  return String(value)
    .toLowerCase()
    .replace(/\b([a-z])/g, char => char.toUpperCase());
};

const formatAddressPart = value => {
  if (!value) return '';

  return String(value)
    .trim()
    .split(/\s+/)
    .map(part => {
      if (/^[A-Z]{2}$/.test(part)) return part;
      if (/^\d+[A-Za-z]?$/.test(part)) return part;
      return toTitleCase(part);
    })
    .join(' ');
};

const formatAddress = address => {
  if (!address) return '—';

  const {
    street,
    line1,
    line2,
    city,
    state,
    zip,
    postal_code: postalCode,
  } = address;

  const streetPart = [street || line1, line2]
    .filter(Boolean)
    .map(formatAddressPart)
    .join(' ');

  const cityPart = city ? toTitleCase(city) : '';
  const statePart = state ? String(state).toUpperCase() : '';
  const zipPart = zip || postalCode || '';

  if (!streetPart && !cityPart && !statePart && !zipPart) return '—';

  return (
    <>
      {streetPart && (
        <div>
          {streetPart}
          {(cityPart || statePart || zipPart) && ','}
        </div>
      )}
      {(cityPart || statePart || zipPart) && (
        <div>
          {cityPart}
          {cityPart && ','}
          {statePart && ` ${statePart}`}
          {zipPart && ` ${zipPart}`}
        </div>
      )}
    </>
  );
};

const ITF_SECTION_TITLES = {
  compensation: 'Disability compensation',
  pension: 'Pension',
  survivor: 'Survivor',
};

const ITF_BENEFIT_LABELS = {
  compensation: 'Disability compensation (VA Form 21-526EZ)',
  pension: 'Pension (VA Form 21P-527EZ)',
  survivor: 'Survivor benefits',
};

const normalizeItfArray = rawItf => {
  if (!Array.isArray(rawItf)) return [];

  return rawItf
    .map(entry => {
      const attrs = entry?.data?.attributes || entry?.attributes || entry;

      const benefitType =
        attrs?.type || entry?.benefitType || entry?.benefit_type;
      if (!benefitType) return null;

      const itfDate = attrs?.creationDate || attrs?.itfDate || attrs?.itf_date;
      const expirationDate =
        attrs?.expirationDate || attrs?.expiration_date || null;

      return {
        benefitType,
        benefit: ITF_BENEFIT_LABELS[benefitType] || benefitType,
        itfDate: itfDate || null,
        expirationDate,
        status: attrs?.status || null,
      };
    })
    .filter(Boolean);
};

const mapClaimantOverview = raw => ({
  firstName: raw?.first_name,
  lastName: raw?.last_name,
  dateOfBirth: raw?.birth_date,
  veteranSsn: raw?.ssn,
  claimantSsn: raw?.claimant_ssn,
  claimantDateOfBirth: raw?.claimant_birth_date,
  claimantFullName:
    raw?.claimant_first_name || raw?.claimant_last_name
      ? { first: raw?.claimant_first_name, last: raw?.claimant_last_name }
      : null,
  phone: raw?.phone,
  email: raw?.email,
  address: raw?.address || null,
  representativeName: raw?.representative_name || null,
  representative: raw?.representative ?? raw?.is_representative,
  intentToFile: normalizeItfArray(raw?.itf),
  poaRequests: raw?.poa_requests,
});

const formatEmail = email => {
  if (!email) return 'No email on file';

  const [localPart, domain] = String(email).split('@');
  if (!domain) return <a href={`mailto:${email}`}>{email}</a>;

  return (
    <a href={`mailto:${email}`}>
      {localPart}
      <wbr />@{domain}
    </a>
  );
};

const formatItfDate = value => {
  if (!value) return '—';

  const raw = typeof value === 'string' ? value : String(value);
  const dateOnly = raw.includes('T') ? raw.split('T')[0] : raw;

  let d;

  if (/^\d{8}$/.test(dateOnly)) {
    const y = Number(dateOnly.slice(0, 4));
    const m = Number(dateOnly.slice(4, 6)) - 1;
    const day = Number(dateOnly.slice(6, 8));
    d = new Date(y, m, day);
  } else {
    d = new Date(dateOnly);
  }

  if (Number.isNaN(d.getTime())) return '—';

  return d.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

const statusAlert = claim => {
  if (
    claim.representative &&
    requestsContainStatus('pending', claim.poaRequests)
  ) {
    return (
      <div className="vads-u-margin-y--0">
        <va-alert status="warning">
          <h2>There is a pending representation request</h2>
          <p>
            To establish representation with this claimant, review and accept
            the pending representation request.
          </p>
          <va-link-action
            href={`/representative/representation-requests/${
              claim.poaRequests?.find(r => !r.resolution)?.id
            }`}
            text="Review the representation request"
            type="primary"
          />
        </va-alert>
      </div>
    );
  }

  return null;
};

const ClaimantOverviewPage = () => {
  const {
    authorized = true,
    claimant = null,
    claimantId,
    notRepresented = false,
  } = useLoaderData() || {};
  const unauthorized = authorized === false;

  const wrapperFirstName = claimant?.firstName || '—';
  const wrapperLastName = claimant?.lastName || '—';

  const claimantInfoData = {
    'Last 4 digits of Social Security Number': claimant?.veteranSsn
      ? String(claimant.veteranSsn).slice(-4)
      : '—',
    'Date of birth': formatItfDate(claimant?.dateOfBirth),
    Address: formatAddress(claimant?.address),
    Phone: claimant?.phone ? (
      <va-telephone contact={claimant.phone} not-clickable />
    ) : (
      '—'
    ),
    Email: formatEmail(claimant?.email),
  };

  if (unauthorized && notRepresented) {
    return <ClaimantNotFound />;
  }

  document.title = `Claimant ${wrapperLastName}, ${wrapperFirstName}'s claimant overview | Accredited Representative Portal | Veterans Affairs`;
  return (
    <section className="vads-u-width--full claimant-overview">
      <div className="claimant-overview__breadcrumbs">
        <VaBreadcrumbs
          breadcrumbList={claimantOverviewBC}
          homeVeteransAffairs={false}
          wrapping
        />
      </div>

      <ClaimantDetailsWrapper
        firstName={wrapperFirstName}
        lastName={wrapperLastName}
      >
        {!unauthorized && (
          <>
            <h2 className="vads-u-margin-top--0 vads-u-margin-bottom--3">
              Claimant overview
            </h2>

            {statusAlert(claimant)}

            <section className="vads-u-margin-top--4">
              <h3
                id="claimant-information-heading"
                className="vads-u-margin-top--0 claimant-overview__section-heading"
              >
                Claimant information
              </h3>

              <div className="vads-grid-row">
                <div className="vads-grid-col-11">
                  <KeyValueTable
                    headingId="claimant-information-heading"
                    data={claimantInfoData}
                  />
                </div>
              </div>
            </section>

            <section className="vads-u-margin-top--4">
              <h3
                id="representative-information-heading"
                className="vads-u-margin-top--0 claimant-overview__section-heading"
              >
                Representative information
              </h3>

              <div className="vads-grid-row">
                <div className="vads-grid-col-11">
                  <KeyValueTable
                    headingId="representative-information-heading"
                    data={{
                      Representative: claimant?.representativeName || '—',
                    }}
                  />
                </div>
              </div>

              <div className="vads-u-margin-top--4">
                <va-additional-info trigger="The portal doesn’t check for limited representation">
                  <p className="vads-u-margin-y--0 vads-u-font-size--base vads-u-line-height--4">
                    Limited representation means that the representation is only
                    for a specific claim or claims. Check with the claimant or
                    in VBMS for any existing limited representation.
                  </p>
                </va-additional-info>
              </div>
            </section>

            <section className="vads-u-margin-top--4">
              <h3
                id="intent-to-file-heading"
                className="vads-u-margin-top--0 vads-u-margin-bottom--1"
              >
                Intent to file status
              </h3>

              {claimant?.intentToFile?.length ? (
                <>
                  {claimant.intentToFile.map(itf => (
                    <div
                      key={`${itf.benefitType}-${itf.itfDate || 'no-date'}-${
                        itf.expirationDate || 'no-exp'
                      }`}
                      style={{ marginTop: 'var(--vads-spacing-2p5)' }}
                    >
                      {claimant?.intentToFile?.length > 1 && (
                        <h4
                          id={`intent-to-file-heading-${itf.benefitType}`}
                          className="vads-u-margin-top--0 vads-u-margin-bottom--2p5"
                        >
                          {ITF_SECTION_TITLES[itf.benefitType] ||
                            itf.benefitType}
                        </h4>
                      )}

                      <div className="vads-grid-row">
                        <div className="vads-grid-col-11">
                          <KeyValueTable
                            headingId={`intent-to-file-heading-${itf.benefitType} intent-to-file-heading`}
                            data={{
                              Benefit: itf.benefit || '—',
                              'ITF date': formatItfDate(itf.itfDate),
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              ) : (
                <>
                  <p className="vads-u-margin-top--0 vads-u-margin-bottom--2">
                    This claimant doesn’t have an intent to file. To establish
                    an intent to file within minutes, submit online VA Form
                    21-0966.
                  </p>

                  <a
                    className="vads-c-action-link--blue"
                    href={`/representative/representative-form-upload/submit-va-form-21-0966/introduction?claimantId=${claimantId}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Submit online VA Form 21-0966 (opens in a new tab)
                  </a>
                </>
              )}
            </section>
          </>
        )}
      </ClaimantDetailsWrapper>
    </section>
  );
};

ClaimantOverviewPage.loader = async ({ params }) => {
  const claimantId = params?.claimantId;
  if (!claimantId) throw new Response('Not Found', { status: 404 });

  try {
    const overviewRes = await api.getClaimantOverview(claimantId);

    if (overviewRes.status === 401) throw overviewRes;
    if (!overviewRes.ok) throw overviewRes;

    const overviewJson = await overviewRes.json();
    const overviewRaw = overviewJson?.data || overviewJson;
    const claimant = mapClaimantOverview(overviewRaw);

    return { authorized: true, claimant, claimantId };
  } catch (err) {
    if (err instanceof Response && err.status === 403) {
      return {
        authorized: false,
        claimant: null,
        claimantId,
        notRepresented: true,
      };
    }

    throw err;
  }
};

export default ClaimantOverviewPage;
